import { useMemo } from 'react';

import { useMissionSections } from '@/hooks/useMissionSections';
import { useProfile } from '@/hooks/useProfile';
import type { QuestSection } from '@/lib/types';

export interface QuestTotals {
  completed: number;
  total: number | undefined;
  mergedSections: QuestSection[] | undefined;
}

/**
 * Single source of truth for "quests completed / total". Counts from
 * `useMissionSections()` so the client-synthesized "Connect Telegram Bot"
 * quest is included, matching every other mission surface.
 */
export function useQuestTotals(): QuestTotals {
  const { data: profile } = useProfile();
  const { data: mergedSections } = useMissionSections();

  const total = useMemo(
    () => mergedSections?.reduce((acc, s) => acc + s.quests.length, 0),
    [mergedSections],
  );

  const completed = useMemo(
    () =>
      mergedSections?.reduce(
        (acc, s) => acc + s.quests.filter((q) => q.status === 'completed').length,
        0,
      ) ?? profile?.missionsCompleted ?? 0,
    [mergedSections, profile?.missionsCompleted],
  );

  return { completed, total, mergedSections };
}
